import { useLocation } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";

export function PrivacyPage() {
  usePageMeta({
    title: "Privacy Policy — QuotedsArrow",
    description: "Read the QuotedsArrow privacy policy.",
  });

  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-stone-50 font-sans">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-stone-50/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-900 text-white text-sm font-bold">
              →
            </div>
            <span className="font-serif text-lg text-stone-900">QuotedsArrow</span>
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-stone-500 hover:text-stone-900 transition"
          >
            ← Back
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="mb-10">
          <h1 className="font-serif text-4xl text-stone-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-stone-500">Last updated: May 21, 2026</p>
        </div>

        <div className="space-y-10 text-stone-700 leading-relaxed">
          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Overview</h2>
            <p>
              QuotedsArrow ("we", "our", or "us") is a literary quote discovery app. We take your
              privacy seriously. This Privacy Policy explains what personal data we collect, how we
              use it, and what rights you have. If you have any questions, contact us at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Data We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Account information</h3>
                <p>
                  When you create an account, we collect your email address and, if you sign in
                  with Google, your display name and profile photo. You may also set a username
                  and upload an avatar photo after signing up.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Usage data</h3>
                <p>
                  We store the quotes you save to your library, the quotes you mark as favorites,
                  and any quotes you pin to your profile. This data is what powers your personal
                  recommendations.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Notification preferences</h3>
                <p>
                  If you enable daily quote emails, we store your preferred delivery hour and
                  timezone so we can send your quote at the right time.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Quote submissions</h3>
                <p>
                  If you submit a quote for consideration in the community library, we store the
                  quote text, author, era, source, and any notes you provide, along with your
                  user ID and submission timestamp.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">How We Use Your Data</h2>
            <ul className="space-y-2 list-none pl-0">
              {[
                "To identify you across sessions and provide a personalised experience.",
                "To generate AI-powered quote recommendations based on your saved and favorited quotes. We use OpenAI's API to compute semantic similarity between quotes — your saved quotes are converted into numerical representations (embeddings) to find similar ones. We do not share your personal identity with OpenAI.",
                "To send daily quote emails when you have enabled that feature, via the Resend email delivery service.",
                "To display your submitted quotes to other community members once they are approved by our moderation team.",
                "To measure aggregate usage and improve the product (page view analytics via Plausible, which collects no personal data).",
              ].map((item, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Third-Party Processors</h2>
            <p className="mb-4">
              We share your data only with the service providers necessary to operate the app:
            </p>
            <div className="overflow-hidden rounded-2xl border border-stone-200">
              <table className="w-full text-sm">
                <thead className="bg-stone-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-stone-700">Provider</th>
                    <th className="px-4 py-3 text-left font-semibold text-stone-700">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold text-stone-700">Data shared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {[
                    ["Firebase (Google)", "Authentication & file storage", "Email, display name, profile photo"],
                    ["OpenAI", "Quote embeddings & recommendations", "Quote text only (no personal identifiers)"],
                    ["Resend", "Email delivery", "Email address, quote content"],
                    ["Plausible Analytics", "Anonymous page-view analytics", "None (no personal data collected)"],
                  ].map(([provider, purpose, data]) => (
                    <tr key={provider} className="bg-white">
                      <td className="px-4 py-3 font-medium text-stone-900">{provider}</td>
                      <td className="px-4 py-3 text-stone-600">{purpose}</td>
                      <td className="px-4 py-3 text-stone-500">{data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Data Retention</h2>
            <p>
              We retain your data for as long as your account is active. If you delete your account,
              we will delete your personal data within 30 days, except where retention is required
              by law. Approved community quotes you submitted may remain in the library as
              part of the shared collection, attributed only to the author (not to your account).
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Your Rights</h2>
            <p className="mb-3">
              Depending on where you live, you may have the right to access, correct, or delete
              the personal data we hold about you. You may also have the right to object to or
              restrict certain processing, or to request a copy of your data in a portable format.
            </p>
            <p>
              To exercise any of these rights, email us at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Cookies</h2>
            <p>
              We do not use tracking cookies. Our analytics provider (Plausible) is cookieless
              and does not collect personally identifiable information. Firebase stores a session
              token in your browser's local storage to keep you signed in — this is essential for
              the app to function.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Children's Privacy</h2>
            <p>
              QuotedsArrow is not directed at children under 13. We do not knowingly collect
              personal data from children under 13. If you believe a child has provided us
              with personal data, please contact us and we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes by updating the date at the top of this page. Continued use of the app after
              a change constitutes your acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-stone-900 mb-3">Contact</h2>
            <p>
              For any privacy-related questions or requests, please reach out to us at{" "}
              <a
                href="mailto:privacy@quotedsarrow.com"
                className="text-stone-900 underline underline-offset-2 hover:text-stone-600 transition"
              >
                privacy@quotedsarrow.com
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-stone-200 bg-stone-50 py-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between">
          <span className="text-xs text-stone-400">© 2026 QuotedsArrow</span>
          <div className="flex gap-4 text-xs text-stone-500">
            <button onClick={() => navigate("/privacy")} className="hover:text-stone-900 transition">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-stone-900 transition">Terms</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
